import { useCallback, useEffect, useMemo, useState } from "react";
import api, { fmtINR } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PlusCircle, Pencil, Trash2, RefreshCw, X, Bed as BedIcon, ShieldAlert } from "lucide-react";

/**
 * Bed Management — visual grid + admin CRUD.
 *  • Every card shows the current status colour and the bed / room number.
 *  • Admin / Super-Admin sees an Edit + Delete pencil on each card and an
 *    "Add Bed" button on the ward header.
 *  • Delete is blocked server-side when the bed is occupied.
 */

const STATUSES = ["available", "occupied", "cleaning", "reserved", "blocked", "maintenance"];
const STATUS_COLOR = {
  available:   "bg-teal-100 text-teal-800 border-teal-300",
  occupied:    "bg-red-100 text-red-800 border-red-300",
  cleaning:    "bg-amber-100 text-amber-800 border-amber-300",
  reserved:    "bg-blue-100 text-blue-800 border-blue-300",
  blocked:     "bg-slate-200 text-slate-700 border-slate-300",
  maintenance: "bg-purple-100 text-purple-800 border-purple-300",
};

const EMPTY_FORM = { bed_no: "", room_no: "", floor: "", ward: "General", rate: 0, status: "available" };

export default function BedManagement() {
  const { user } = useAuth();
  const canEdit = ["admin", "super_admin"].includes(user?.role || "");
  const [beds, setBeds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(null); // { mode: "add"|"edit", data: {...} }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/beds");
      setBeds(r.data || []);
    } catch { toast.error("Failed to load beds"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const g = {};
    for (const b of beds) (g[b.ward || "General"] = g[b.ward || "General"] || []).push(b);
    for (const list of Object.values(g)) list.sort((a, b) => (a.bed_no || "").localeCompare(b.bed_no || "", undefined, { numeric: true }));
    return g;
  }, [beds]);

  const counts = useMemo(() => {
    const c = {};
    STATUSES.forEach(s => c[s] = 0);
    for (const b of beds) if (c[b.status] !== undefined) c[b.status]++;
    return c;
  }, [beds]);

  const openAdd  = (ward)  => setDialog({ mode: "add", data: { ...EMPTY_FORM, ward: ward || "General" } });
  const openEdit = (bed)   => setDialog({ mode: "edit", data: { ...EMPTY_FORM, ...bed } });
  const close    = ()      => setDialog(null);

  const save = async () => {
    const { mode, data } = dialog;
    const payload = { ...data, rate: Number(data.rate) || 0 };
    if (!payload.bed_no?.trim()) return toast.error("Bed No. is required");
    try {
      if (mode === "add") {
        await api.post("/beds", payload);
        toast.success(`Bed ${payload.bed_no} added`);
      } else {
        await api.put(`/beds/${data.id}`, payload);
        toast.success(`Bed ${payload.bed_no} updated`);
      }
      close();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const delBed = async (b) => {
    if (!window.confirm(`Delete bed ${b.bed_no} (${b.ward})?\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/beds/${b.id}`);
      toast.success(`Bed ${b.bed_no} deleted`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const quickStatus = async (b, next) => {
    try {
      await api.put(`/beds/${b.id}`, { status: next });
      // Optimistic local flip so the UI feels instant.
      setBeds((rows) => rows.map(x => x.id === b.id ? { ...x, status: next } : x));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status change failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="bed-management">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
            <BedIcon className="w-6 h-6 text-blue-700"/> Bed Management
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            {beds.length} beds across {Object.keys(grouped).length} ward{Object.keys(grouped).length === 1 ? "" : "s"}
            {!canEdit && <span className="ml-2 inline-flex items-center gap-1 text-amber-700"><ShieldAlert className="w-3 h-3"/> Read-only for your role</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} className="rounded-sm h-9" data-testid="bed-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1"/> Refresh
          </Button>
          {canEdit && (
            <Button onClick={() => openAdd()} className="bg-blue-700 hover:bg-blue-800 rounded-sm h-9" data-testid="bed-add-btn">
              <PlusCircle className="w-4 h-4 mr-1"/> Add Bed
            </Button>
          )}
        </div>
      </div>

      {/* Counters */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {STATUSES.map((s) => (
          <div key={s} className={`border rounded-sm p-2 ${STATUS_COLOR[s]}`}>
            <div className="text-[10px] uppercase tracking-widest opacity-70">{s}</div>
            <div className="font-mono text-lg mt-0.5">{counts[s] || 0}</div>
          </div>
        ))}
      </div>

      {loading && <div className="text-center text-sm text-slate-500 py-8">Loading beds…</div>}
      {!loading && beds.length === 0 && (
        <div className="border-2 border-dashed border-slate-300 rounded-sm p-10 text-center text-slate-500">
          No beds configured yet. {canEdit && <>Tap <b>Add Bed</b> to create the first one.</>}
        </div>
      )}

      {Object.entries(grouped).map(([ward, list]) => (
        <div key={ward} className="bg-white border rounded-sm">
          <div className="p-3 border-b flex items-center justify-between">
            <div className="label-tiny">
              {ward} · {list.length} beds
              <span className="ml-2 text-slate-400 font-normal">
                {list[0]?.rate ? `${fmtINR(list[0].rate)}/day` : ""}
              </span>
            </div>
            {canEdit && (
              <button
                onClick={() => openAdd(ward)}
                className="text-blue-700 hover:underline text-xs flex items-center gap-1"
                data-testid={`bed-add-to-${ward}`}
              >
                <PlusCircle className="w-3 h-3"/> Add to {ward}
              </button>
            )}
          </div>
          <div className="p-3 grid grid-cols-3 sm:grid-cols-5 md:grid-cols-8 lg:grid-cols-10 gap-2">
            {list.map((b) => (
              <div key={b.id} className={`border rounded-sm p-2 text-center text-xs ${STATUS_COLOR[b.status] || ""} relative group`} data-testid={`bed-${b.bed_no}`}>
                <div className="font-mono font-semibold text-sm">{b.bed_no}</div>
                <div className="text-[10px] opacity-80">{b.room_no || "-"}</div>
                <div className="text-[9px] uppercase tracking-widest mt-0.5">{b.status}</div>
                {canEdit && (
                  <div className="absolute top-0.5 right-0.5 opacity-0 group-hover:opacity-100 flex gap-0.5 transition">
                    <button
                      onClick={() => openEdit(b)}
                      className="w-5 h-5 bg-white/90 border rounded-sm flex items-center justify-center hover:bg-blue-100"
                      title="Edit"
                      data-testid={`bed-edit-${b.bed_no}`}
                    >
                      <Pencil className="w-3 h-3 text-blue-700"/>
                    </button>
                    <button
                      onClick={() => delBed(b)}
                      className="w-5 h-5 bg-white/90 border rounded-sm flex items-center justify-center hover:bg-red-100"
                      title="Delete"
                      data-testid={`bed-del-${b.bed_no}`}
                    >
                      <Trash2 className="w-3 h-3 text-red-700"/>
                    </button>
                  </div>
                )}
                {canEdit && b.status !== "occupied" && (
                  <select
                    value={b.status}
                    onChange={(e) => quickStatus(b, e.target.value)}
                    className="mt-1 text-[9px] w-full border rounded-sm bg-white/70 py-0.5"
                    data-testid={`bed-status-${b.bed_no}`}
                  >
                    {STATUSES.filter(s => s !== "occupied").map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {dialog && (
        <BedDialog
          mode={dialog.mode}
          data={dialog.data}
          onChange={(patch) => setDialog({ ...dialog, data: { ...dialog.data, ...patch } })}
          onClose={close}
          onSave={save}
        />
      )}
    </div>
  );
}

function BedDialog({ mode, data, onChange, onClose, onSave }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-sm w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="bed-dialog">
        <div className="border-b px-4 py-3 flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold">{mode === "add" ? "Add Bed" : `Edit Bed ${data.bed_no}`}</h2>
          <button onClick={onClose}><X className="w-4 h-4"/></button>
        </div>
        <div className="p-4 grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-xs">Bed No. *</Label>
            <Input value={data.bed_no} onChange={(e) => onChange({ bed_no: e.target.value })} className="h-9" data-testid="bed-form-bed-no"/>
          </div>
          <div>
            <Label className="text-xs">Room No.</Label>
            <Input value={data.room_no} onChange={(e) => onChange({ room_no: e.target.value })} className="h-9" data-testid="bed-form-room"/>
          </div>
          <div>
            <Label className="text-xs">Floor</Label>
            <Input value={data.floor} onChange={(e) => onChange({ floor: e.target.value })} className="h-9"/>
          </div>
          <div>
            <Label className="text-xs">Ward</Label>
            <Input value={data.ward} onChange={(e) => onChange({ ward: e.target.value })} className="h-9" data-testid="bed-form-ward"/>
          </div>
          <div>
            <Label className="text-xs">Rate / day (₹)</Label>
            <Input type="number" min="0" value={data.rate} onChange={(e) => onChange({ rate: e.target.value })} className="h-9" data-testid="bed-form-rate"/>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Status</Label>
            <select
              value={data.status}
              onChange={(e) => onChange({ status: e.target.value })}
              className="h-9 border rounded-sm text-sm px-2 w-full bg-white"
              data-testid="bed-form-status"
            >
              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {data.status === "occupied" && (
              <p className="text-[11px] text-amber-700 mt-1">
                <ShieldAlert className="w-3 h-3 inline mr-0.5"/>
                Setting status directly to "occupied" here won't auto-link an IPD. Use the IPD admission flow to occupy a bed.
              </p>
            )}
          </div>
        </div>
        <div className="border-t px-4 py-3 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button onClick={onSave} className="bg-blue-700 hover:bg-blue-800 rounded-sm" data-testid="bed-form-save">
            {mode === "add" ? "Add Bed" : "Save Changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}
